import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Nav, Row, Spinner } from "react-bootstrap";
import { FaBell, FaClock, FaComments, FaFileAlt, FaFolder, FaQuestionCircle, FaSyncAlt, FaUsers } from "react-icons/fa";
import { useParams } from "react-router-dom";
import {
  downloadOobAdminEventArtifact,
  getOobAdminEventQuestions,
  getOobAdminLocalEventChannelMessages,
  getOobAdminLocalEventChannels,
  getOobEvent,
  getOobEventArtifacts,
  getOobEventParticipants,
  OobEventArtifactDto,
  OobEventDto,
  OobEventQuestionDto,
  OobLocalEventChannelDto,
  OobLocalEventMessageDto,
} from "@/api/outOfBandApi";
import { formatDuration, OobEventMetricStrip, OobEventWorkspaceHeader } from "@/components/Oob/OobEventWorkspace";

const fmt = (value?: string) => value ? new Date(value).toLocaleString() : "-";
type PreviewSection = "updates" | "channels" | "questions" | "files";

const AdminOobEventPreview: React.FC = () => {
  const { eventGuid } = useParams<{ eventGuid: string }>();
  const [loading, setLoading] = useState(true);
  const [refreshingChannel, setRefreshingChannel] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [event, setEvent] = useState<OobEventDto | null>(null);
  const [participantCount, setParticipantCount] = useState(0);
  const [artifacts, setArtifacts] = useState<OobEventArtifactDto[]>([]);
  const [questions, setQuestions] = useState<OobEventQuestionDto[]>([]);
  const [channels, setChannels] = useState<OobLocalEventChannelDto[]>([]);
  const [selectedChannelGuid, setSelectedChannelGuid] = useState("");
  const [messages, setMessages] = useState<OobLocalEventMessageDto[]>([]);
  const [section, setSection] = useState<PreviewSection>("updates");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!eventGuid) {
        setError("Missing event identifier.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const loadedEvent = await getOobEvent(eventGuid);
        const [loadedArtifacts, loadedQuestions, loadedParticipants, loadedChannels] = await Promise.all([
          getOobEventArtifacts(eventGuid),
          getOobAdminEventQuestions(eventGuid),
          getOobEventParticipants(eventGuid),
          loadedEvent.deliveryStatus === "LocalActive" ? getOobAdminLocalEventChannels(eventGuid) : Promise.resolve([]),
        ]);
        if (cancelled) return;

        setEvent(loadedEvent);
        setArtifacts(loadedArtifacts.filter((artifact) => artifact.provisioningStatus === "Provisioned"));
        setQuestions(loadedQuestions.filter((question) => question.isPublished));
        setParticipantCount(loadedParticipants.length);
        setChannels(loadedChannels);
        const initialChannelGuid = loadedChannels[0]?.channelGuid || "";
        setSelectedChannelGuid(initialChannelGuid);
        if (initialChannelGuid) {
          setMessages(await getOobAdminLocalEventChannelMessages(eventGuid, initialChannelGuid));
        } else {
          setMessages([]);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.error || err?.message || "Failed to load event preview.");
          setEvent(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => { cancelled = true; };
  }, [eventGuid]);

  const publishedUpdates = useMemo(() => event?.updates.filter((update) => !update.effEndDt && !update.retractedDt) ?? [], [event]);
  const artifactsByType = useMemo(() => {
    const grouped = new Map<string, OobEventArtifactDto[]>();
    for (const artifact of artifacts) {
      const category = artifact.artifactType || "Other";
      const categoryArtifacts = grouped.get(category) ?? [];
      categoryArtifacts.push(artifact);
      grouped.set(category, categoryArtifacts);
    }
    return Array.from(grouped.entries()).sort(([left], [right]) => left.localeCompare(right));
  }, [artifacts]);

  const selectChannel = async (channelGuid: string) => {
    if (!eventGuid) return;
    setSelectedChannelGuid(channelGuid);
    setError(null);
    try {
      setMessages(await getOobAdminLocalEventChannelMessages(eventGuid, channelGuid));
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to load channel messages.");
    }
  };

  const refreshChannel = async () => {
    if (!eventGuid || !selectedChannelGuid) return;
    setRefreshingChannel(true);
    try {
      const [freshMessages, freshChannels] = await Promise.all([
        getOobAdminLocalEventChannelMessages(eventGuid, selectedChannelGuid),
        getOobAdminLocalEventChannels(eventGuid),
      ]);
      setMessages(freshMessages);
      setChannels(freshChannels);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to refresh channel messages.");
    } finally {
      setRefreshingChannel(false);
    }
  };

  const downloadArtifact = async (artifact: OobEventArtifactDto) => {
    if (!eventGuid) return;
    try {
      await downloadOobAdminEventArtifact(eventGuid, artifact.artifactGuid, artifact.artifactName);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to download event file.");
    }
  };

  if (loading) {
    return <Container className="py-4" style={{ maxWidth: 1000 }}><div className="text-center py-5"><Spinner animation="border" /><div className="mt-2">Loading event preview...</div></div></Container>;
  }

  if (!event) {
    return <Container className="py-4" style={{ maxWidth: 1000 }}>
      <div className="d-flex justify-content-between align-items-center mb-3"><h4 className="mb-0">Event Preview</h4><Button as="a" href="/admin/oob" variant="outline-secondary" size="sm">Back to OOB Admin</Button></div>
      {error && <Alert variant="danger">{error}</Alert>}
    </Container>;
  }

  const isLocalEvent = event.deliveryStatus === "LocalActive";
  const selectedChannel = channels.find((channel) => channel.channelGuid === selectedChannelGuid);

  return (
    <Container fluid className="px-4 py-3 oob-participant-page">
      <div className="d-flex justify-content-end mb-2"><Button as="a" href="/admin/oob" variant="outline-secondary" size="sm">Back to OOB Admin</Button></div>
      {error && <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>{error}</Alert>}
      <OobEventWorkspaceHeader event={event} compact />
      <OobEventMetricStrip compact metrics={[
        { label: "Updates", value: publishedUpdates.length, icon: FaBell },
        { label: "Members", value: participantCount, icon: FaUsers },
        { label: "Files", value: artifacts.length, icon: FaFileAlt },
        { label: "Duration", value: formatDuration(event.activatedDt || event.createDt), icon: FaClock },
      ]} />

      {isLocalEvent ? (
        <Row className="g-3 oob-participant-workspace">
          <Col lg={3}>
            <Nav variant="pills" className="flex-column oob-participant-workspace__nav" aria-label="Preview event sections">
              <Nav.Item><Nav.Link active={section === "updates"} onClick={() => setSection("updates")}><FaBell /><span>Event Updates ({publishedUpdates.length})</span></Nav.Link></Nav.Item>
              <Nav.Item><Nav.Link active={section === "channels"} onClick={() => setSection("channels")}><FaComments /><span>Channels ({channels.reduce((total, channel) => total + channel.messageCount, 0)})</span></Nav.Link></Nav.Item>
              {channels.map((channel) => <Nav.Item key={channel.channelGuid} className="oob-participant-workspace__subtab"><Nav.Link active={section === "channels" && selectedChannelGuid === channel.channelGuid} onClick={() => { setSection("channels"); void selectChannel(channel.channelGuid); }}><FaComments /><span>{channel.displayName} ({channel.messageCount})</span></Nav.Link></Nav.Item>)}
              <Nav.Item><Nav.Link active={section === "questions"} onClick={() => setSection("questions")}><FaQuestionCircle /><span>Questions and Answers ({questions.length})</span></Nav.Link></Nav.Item>
              <Nav.Item><Nav.Link active={section === "files"} onClick={() => setSection("files")}><FaFileAlt /><span>Files ({artifacts.length})</span></Nav.Link></Nav.Item>
            </Nav>
          </Col>
          <Col lg={6}>
            <div className="oob-participant-workspace__content">
              {section === "updates" && <Card><Card.Header><strong>Pinned / Latest Instructions</strong></Card.Header><Card.Body>
                {publishedUpdates.length === 0 ? <div className="text-muted">No updates posted yet.</div> : <div className="d-flex flex-column gap-2">{publishedUpdates.map((update) => <Card key={update.updateGuid} border={update.isPinned ? "danger" : "light"}><Card.Body>
                  <div className="d-flex justify-content-between align-items-center gap-2"><div className="fw-semibold">{update.messageTitle}</div><div className="d-flex gap-2">{update.isPinned && <Badge bg="danger">Pinned</Badge>}<Badge bg={update.importance === "Critical" ? "danger" : update.importance === "Important" ? "warning" : "secondary"}>{update.importance}</Badge></div></div>
                  <div className="mt-2">{update.messageBody}</div>{update.linkUrl && <div className="mt-1"><a href={update.linkUrl} target="_blank" rel="noopener noreferrer">{update.linkLabel || update.linkUrl}</a></div>}<div className="small text-muted mt-2">Posted {fmt(update.createDt)}</div>
                </Card.Body></Card>)}</div>}
              </Card.Body></Card>}

              {section === "channels" && <Card className="oob-local-channel-participant"><Card.Header className="d-flex align-items-center justify-content-between gap-2">
                <div className="d-flex align-items-center gap-2"><FaComments /><strong>{selectedChannel?.displayName || "Local Event Channel"}</strong></div>
                <Button size="sm" variant="outline-secondary" onClick={() => void refreshChannel()} disabled={refreshingChannel || !selectedChannelGuid} aria-label="Refresh channel messages" title="Refresh channel messages"><FaSyncAlt className={refreshingChannel ? "oob-refresh-icon--spinning" : ""} /></Button>
              </Card.Header><Card.Body>
                {channels.length === 0 ? <div className="text-muted">No local channels are available.</div> : <>
                  {selectedChannel?.description && <div className="small text-muted mb-2">{selectedChannel.description}</div>}
                  <div className="oob-local-channel-messages">{messages.length === 0 ? <div className="text-muted text-center py-3">No messages yet.</div> : messages.map((message) => <div key={message.messageGuid} className="oob-local-channel-message-row"><div className="oob-local-channel-message"><div className="oob-local-channel-message__meta"><strong>{message.authorDisplayName || message.authorEmail}</strong><time dateTime={message.createDt}>{fmt(message.createDt)}</time></div><div className="oob-local-channel-message__body">{message.messageBody}</div></div></div>)}</div>
                </>}
              </Card.Body></Card>}

              {section === "questions" && <Card><Card.Header className="d-flex align-items-center gap-2"><FaQuestionCircle /><strong>Questions and Answers</strong></Card.Header><Card.Body>
                {questions.length === 0 ? <div className="text-muted">No published questions or answers.</div> : <div className="d-flex flex-column gap-3">{questions.map((question) => <div key={question.questionGuid} className="border-bottom pb-2"><div className="fw-semibold">{question.questionText}</div>{question.answerText && <div className="mt-1">{question.answerText}</div>}</div>)}</div>}
              </Card.Body></Card>}

              {section === "files" && <Card><Card.Header className="d-flex align-items-center gap-2"><FaFileAlt /><strong>Event Files</strong></Card.Header><Card.Body>
                {artifacts.length === 0 ? <div className="text-muted">No event files have been published yet.</div> : <div className="oob-file-tree">{artifactsByType.map(([folder, folderArtifacts]) => <div key={folder} className="oob-file-tree__folder"><div className="d-flex align-items-center gap-2 fw-semibold mb-2"><FaFolder /><span>{folder}</span></div><div className="oob-file-tree__files">{folderArtifacts.map((artifact) => <div key={artifact.artifactGuid} className="oob-file-tree__file"><div className="min-w-0"><div className="fw-semibold text-break">{artifact.artifactName}</div><div className="small text-muted">{fmt(artifact.provisionedDt)}</div></div><Button size="sm" variant="outline-primary" onClick={() => void downloadArtifact(artifact)}>Download</Button></div>)}</div></div>)}</div>}
              </Card.Body></Card>}
            </div>
          </Col>
          <Col lg={3} className="oob-participant-workspace__aside"><Card className="oob-participant-event-info"><Card.Header>Event Information</Card.Header><Card.Body>
            <div className="small text-muted mb-1">Status</div><div className="mb-3">{event.status}</div><div className="small text-muted mb-1">Communication</div><div className="mb-3">Local communication space</div><div className="small text-muted mb-1">Started</div><div>{fmt(event.activatedDt || event.createDt)}</div>
          </Card.Body></Card></Col>
        </Row>
      ) : (
        <Card style={{ maxWidth: 1000, margin: "0 auto" }}><Card.Header><strong>Pinned / Latest Instructions</strong></Card.Header><Card.Body>
          {publishedUpdates.length === 0 ? <div className="text-muted">No updates posted yet.</div> : <div className="d-flex flex-column gap-2">{publishedUpdates.map((update) => <Card key={update.updateGuid} border={update.isPinned ? "danger" : "light"}><Card.Body><div className="d-flex justify-content-between align-items-center gap-2"><div className="fw-semibold">{update.messageTitle}</div><div className="d-flex gap-2">{update.isPinned && <Badge bg="danger">Pinned</Badge>}<Badge bg={update.importance === "Critical" ? "danger" : update.importance === "Important" ? "warning" : "secondary"}>{update.importance}</Badge></div></div><div className="mt-2">{update.messageBody}</div>{update.linkUrl && <div className="mt-1"><a href={update.linkUrl} target="_blank" rel="noopener noreferrer">{update.linkLabel || update.linkUrl}</a></div>}<div className="small text-muted mt-2">Posted {fmt(update.createDt)}</div></Card.Body></Card>)}</div>}
        </Card.Body></Card>
      )}
    </Container>
  );
};

export default AdminOobEventPreview;
